import React, { useEffect, useMemo, useState } from 'react';
import { p, tp, useP } from '../../i18n/useP';
import { useTranslation } from 'react-i18next';
import {
  listHrContracts, listHrPeople,
  createHrContract, updateHrContract, deleteHrContract,
  listPayrollMovements,
} from '../../api/hr';
import { getCompanyTimeYear } from '../../api/hrTime';
import { formatNumber } from '../../utils/format';
import EditableCell from './EditableCell';
import ContractEditModal from './ContractEditModal';
import PersonTh from '../../components/PersonTh';
import { formatDate } from '../../utils/format';
import PageHeader from '../../components/PageHeader';
import CardShell from '../../components/CardShell';
import SearchField from '../../components/SearchField';
import SegmentedToggle from '../../components/SegmentedToggle';
import FilterIcon from '../../components/FilterIcon';
import FilterCount from '../../components/FilterCount';
import PersonName from '../../components/PersonName';
import { useTableSort } from '../../hooks/useTableSort';
import { usePersistentValue } from '../../hooks/usePersistentState';
import { normalizeName, buildSearchKey, contractCoversToday } from '../../utils/personTable';
import './HrPage.css';

const NB_SAL_OPTIONS = [
  { value: '12', label: '12' },
  { value: '13', label: '13' },
  { value: '14', label: '14' },
];

/* Fields available for the bulk-update modal.
   `kind` drives the input rendered + how the value is coerced. */
const BULK_FIELDS = [
  { key: 'title',              label: p('Titre'),                       kind: 'text',     nullable: true  },
  { key: 'department',         label: tp('phrases:p_3d7c87c2af'),                 kind: 'text',     nullable: true  },
  { key: 'start_date',         label: tp('phrases:p_706438abfd'),               kind: 'date',     nullable: false },
  { key: 'end_date',           label: p('Date de fin'),                 kind: 'date',     nullable: true  },
  { key: 'monthly_salary',     label: p('Salaire mensuel (CHF)'),       kind: 'number',   nullable: false },
  { key: 'nb_of_salary',       label: p('Nb de salaires'),              kind: 'nb_sal',   nullable: false },
  { key: 'activity_rate',      label: p('Taux d\'activité (1.0 = 100%)'), kind: 'decimal', nullable: false },
  { key: 'vacation_days',      label: p('Jours de vacances'),           kind: 'int',      nullable: false },
  { key: 'cs_empr_pct',        label: 'CS empr %',                   kind: 'number',   nullable: true  },
  { key: 'cs_empl_pct',        label: 'CS empl %',                   kind: 'number',   nullable: true  },
];

const NULL_DATE = ',,';   // placeholder when a date is not set

const COLUMNS = [
  { key: 'person',             label: p('Collaborateur'),           sortable: true,  align: 'left',  numeric: false, personSort: true },
  { key: 'title',              label: p('Titre'),                   sortable: true,  align: 'left',  numeric: false },
  { key: 'department',         label: tp('phrases:p_3d7c87c2af'),             sortable: true,  align: 'left',  numeric: false },
  { key: 'start_date',         label: tp('phrases:p_f0955314c5'),                   sortable: true,  align: 'left',  numeric: false },
  { key: 'end_date',           label: p('Fin'),                     sortable: true,  align: 'left',  numeric: false },
  { key: 'monthly_salary',     label: p('Salaire / mois'),          sortable: true,  align: 'right', numeric: true  },
  { key: 'nb_of_salary',       label: 'Nb sal.',                 sortable: true,  align: 'right', numeric: true  },
  { key: 'activity_rate',      label: tp('phrases:p_a48842269e'),         sortable: true,  align: 'right', numeric: true  },
  { key: 'vacation_days',      label: p('Jours de vacances'),       sortable: true,  align: 'right', numeric: true,
    title: tp('phrases:p_5d8f36e88c') },
  { key: 'effective_yearly',   label: p('Salaire annuel effectif'), sortable: true,  align: 'right', numeric: true,
    title: tp('phrases:p_bfa06967ef') },
  { key: 'yearly_100',         label: p('Salaire annuel @100%'),    sortable: true,  align: 'right', numeric: true,
    title: tp('phrases:p_5a326f78f7') },
];

function effectiveYearly(c) {
  return Number(c.monthly_salary || 0) * Number(c.nb_of_salary || 0);
}
function yearly100(c) {
  const ar = Number(c.activity_rate || 0);
  if (ar <= 0) return 0;
  return effectiveYearly(c) / ar;
}
// Fraction de l'année [year-01-01 ; year-12-31] couverte par le contrat (0..1).
// Prorata temporis en jours — un contrat ouvert couvre jusqu'au 31.12.

export default function HrContractsPage({ activeTenant, setPage, year, embedded }) {
  const { t } = useTranslation();
  const p = useP();
  const tenantId = activeTenant?.id;
  const [contracts, setContracts] = useState([]);
  const [people, setPeople] = useState([]);
  const [movements, setMovements] = useState([]);
  const [loading, setLoading] = useState(false);
  const sort = useTableSort(`hr_contracts_sort_${tenantId}`, 'last_name_sort');
  const { sortCol, sortDir, handleSort: doSort, sortRows, nameOrder } = sort;
  const [search, setSearch] = useState('');
  // « Tous » par défaut (2026-08-27) : la vue complète est l'état normal,
  // « Actifs » ce qu'on choisit de restreindre. Clé versionnée pour que le
  // nouveau défaut s'applique malgré un choix déjà mémorisé.
  const [status, setStatus] = usePersistentValue(`hr_contracts_status_v2_${tenantId}`, 'all');  // 'active' | 'all'
  const [editingContract, setEditingContract] = useState(null);  // null | 'new' | {id, ...}
  const [selected, setSelected] = useState(() => new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  const [error, setError] = useState(null);

  // Défaut vacances @100% pour les nouveaux contrats — paramètre Company
  // calendar de l'année sélectionnée (fallback 25 si année non saisie).
  const [defaultVacationDays, setDefaultVacationDays] = useState(25);

  function refresh() {
    if (!tenantId) return;
    setLoading(true);
    Promise.all([listHrContracts(tenantId), listHrPeople(tenantId), listPayrollMovements(tenantId)])
      .then(([c, p, m]) => {
        if (!c.error) setContracts(c.data || []);
        if (!p.error) setPeople(p.data || []);
        if (!m.error) setMovements(m.data || []);
      })
      .finally(() => setLoading(false));
  }

  useEffect(refresh, [tenantId]); // eslint-disable-line

  useEffect(() => {
    if (!tenantId || !year) return;
    getCompanyTimeYear(tenantId, year).then(r => {
      const v = r.error ? null : Number(r.data?.params?.vacation_days_full_time);
      setDefaultVacationDays(v > 0 ? v : 25);
    });
  }, [tenantId, year]);

  const personIndex = useMemo(() => {
    const m = {};
    for (const p of people) m[p.id] = p;
    return m;
  }, [people]);

  // Enrich rows with derived fields used for both display and sorting.
  const enrichedRows = useMemo(() => contracts.map(c => {
    const p = personIndex[c.person_id];
    return {
      ...c,
      first_name_sort: p ? (p.first_name || '').toLowerCase() : '',
      last_name_sort:  p ? (p.last_name  || '').toLowerCase() : '',
      _search: buildSearchKey(p?.first_name, p?.last_name, c.department),
      effective_yearly:   effectiveYearly(c),
      yearly_100:         yearly100(c),
    };
  }), [contracts, personIndex]);

  const filteredRows = useMemo(() => {
    let arr = enrichedRows;
    // Actifs = contrat couvrant la date du jour.
    if (status === 'active') arr = arr.filter(r => contractCoversToday(r));
    // Unified search (person or department) — accent-insensitive, filters as you type
    const q = normalizeName(search.trim());
    if (q) arr = arr.filter(r => r._search.includes(q));
    return arr;
  }, [enrichedRows, status, search]);

  const sortedRows = useMemo(
    () => sortRows(filteredRows),
    [filteredRows, sortCol, sortDir], // eslint-disable-line
  );

  function handleSort(col) {
    // Allow synthetic person-sort keys in addition to declared columns
    const known = COLUMNS.find(c => c.key === col && c.sortable)
      || col === 'first_name_sort' || col === 'last_name_sort';
    if (!known) return;
    doSort(col);
  }

  // ── Totals on the currently filtered rows (footer line) ────────────────────
  const totals = useMemo(() => {
    return filteredRows.reduce((acc, c) => {
      acc.monthly_salary     += Number(c.monthly_salary || 0);
      acc.activity_rate      += Number(c.activity_rate || 0);
      acc.effective_yearly   += Number(c.effective_yearly || 0);
      acc.yearly_100         += Number(c.yearly_100 || 0);
      return acc;
    }, {
      monthly_salary: 0, activity_rate: 0, effective_yearly: 0, yearly_100: 0,
    });
  }, [filteredRows]);

  // ── Year synthesis, prorata temporis, on the FILTERED rows ────────────────
  // FTE moyen = Σ activity_rate × fraction de l'année couverte (jours/365-366).

  const allSelected = sortedRows.length > 0 && selected.size === sortedRows.length
    && sortedRows.every(r => selected.has(r.id));
  const someSelected = selected.size > 0 && !allSelected;

  function toggleRow(id) {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }
  function toggleAll() {
    setSelected(prev => {
      if (sortedRows.every(r => prev.has(r.id)) && prev.size === sortedRows.length) {
        return new Set();
      }
      return new Set(sortedRows.map(r => r.id));
    });
  }
  function clearSelection() { setSelected(new Set()); }

  if (!tenantId) return <main className="hr-page"><div className="hr-page__empty">{p('Sélectionner une entreprise.')}</div></main>;

  /* Entête standard de carte (2026-09-01) : boutons en actions, filtres
     derrière l'entonnoir, recherche en dernier. */
  const nFilters = (status === 'active' ? 1 : 0) + (search.trim() ? 1 : 0);
  const stdFilters = (
    <>
      <FilterIcon />
      <SegmentedToggle
        compact
        label={p('Statut')} value={status} onChange={setStatus}
        options={[
          { value: 'all', label: p('Tous') },
          { value: 'active', label: p('Actifs'), tone: 'alert' },
        ]}
      />
      <FilterCount count={nFilters} onClear={() => { setStatus('all'); setSearch(''); }} />
    </>
  );
  const stdSearch = (
    <SearchField value={search} onChange={setSearch}
                 placeholder={t('phrases:p_224c3d8b39')} />
  );
  const controls = (
    <>
      <button
        className="hr-page__add-btn"
        onClick={() => setEditingContract('new')}
        disabled={people.length === 0}
        title={people.length === 0 ? p('Ajouter d\'abord un collaborateur') : p('Ajouter un contrat')}
      >{p('+ Ajouter un contrat')}</button>
      <button
        className="hr-page__bulk-btn"
        onClick={() => setBulkOpen(true)}
        disabled={selected.size === 0}
        title={selected.size === 0 ? p('Sélectionner d\'abord des lignes') : p('Édition groupée')}
      >Édition groupée{selected.size > 0 && ` (${selected.size})`}</button>
    </>
  );

  return (
    <main className={`hr-page${embedded ? ' is-embedded' : ''}`}>
      {!embedded && (
        <PageHeader
          title={
            <>
              Contrats
              <span className="hr-page__readonly-badge">lecture seule</span>
            </>
          }
          actions={setPage && <button className="hr-page__back" onClick={() => setPage('home')}>{p('← Accueil')}</button>}
        />
      )}

      {loading && <div className="hr-page__loading">{t('common:status.loading')}</div>}
      {!loading && contracts.length === 0 && <div className="hr-page__empty">Aucun contrat pour cette entreprise.</div>}
      {!loading && contracts.length > 0 && (
        <>
          <CardShell
            title={<>Contrats <small>· {sortedRows.length} contrats
              {(status !== 'all' || search.trim() !== '') && sortedRows.length !== contracts.length ? p(' · filtrés sur {{count}}', { count: contracts.length }) : ''}
              {selected.size > 0 ? p(' · {{count}} sélectionné(s)', { count: selected.size }) : ''}</small></>}
            primaryAction={controls}
            filters={stdFilters}
            search={stdSearch}
            filterCount={nFilters}
            collapsible storageKey="hr-contracts"
            flush
          >
          <div className="hr-page__table-wrap">
            <table className="hr-page__table hr-page__table--checkable">
              <thead>
                <tr>
                  <th className="hr-page__check-col">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      ref={el => { if (el) el.indeterminate = someSelected; }}
                      onChange={toggleAll}
                      title={allSelected ? p('Tout désélectionner') : p('Tout sélectionner')}
                    />
                  </th>
                  <th className="hr-page__col-sticky hr-page__col-sticky--actions" style={{ width: 44 }}></th>
                  {COLUMNS.map(col => {
                    if (col.personSort) {
                      return (
                        <PersonTh
                          key={col.key}
                          sort={sort}
                          className="hr-page__sort-th hr-page__col-sticky hr-page__col-sticky--person"
                          style={{ textAlign: col.align }}
                        />
                      );
                    }
                    const isSorted = sortCol === col.key;
                    return (
                      <th
                        key={col.key}
                        className={`hr-page__sort-th${isSorted ? ' is-sorted' : ''}`}
                        style={{ textAlign: col.align }}
                        onClick={() => handleSort(col.key)}
                        title={col.title || (col.sortable ? p('Cliquer pour trier') : '')}
                      >
                        <span>{col.label}</span>
                        <span className="hr-page__sort-ind">
                          {isSorted ? (sortDir === 'asc' ? '↑' : '↓') : '↕'}
                        </span>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {sortedRows.map(r => {
                  const p = personIndex[r.person_id];
                  const patch = async (payload) => {
                    const rr = await updateHrContract(tenantId, r.id, payload);
                    if (rr.error) setError(rr.error);
                    // Optimistic local update — avoids full refresh that resets scroll.
                    else setContracts(prev => prev.map(c => c.id === r.id ? rr.data : c));
                  };
                  const isSel = selected.has(r.id);
                  return (
                    <tr key={r.id} className={isSel ? 'is-selected' : ''}>
                      <td className="hr-page__check-col">
                        <input
                          type="checkbox"
                          checked={isSel}
                          onChange={() => toggleRow(r.id)}
                        />
                      </td>
                      <td className="hr-page__col-sticky hr-page__col-sticky--actions" style={{ textAlign: 'center', whiteSpace: 'nowrap', width: 44 }}>
                        <button
                          className="hr-page__row-edit"
                          onClick={() => setEditingContract(r)}
                          title={t('phrases:p_fe5a5d48ef')}
                        >✎</button>
                      </td>
                      <td className="hr-page__col-sticky hr-page__col-sticky--person">{p ? <PersonName firstName={p.first_name} lastName={p.last_name} order={nameOrder} /> : <span className="hr-page__muted">{r.person_id.slice(0, 8)}…</span>}</td>
                      <td>
                        <EditableCell value={r.title} onCommit={v => patch({ title: v })}
                          formatter={v => v || NULL_DATE} />
                      </td>
                      <td>
                        <EditableCell value={r.department} onCommit={v => patch({ department: v })}
                          formatter={v => v || NULL_DATE} />
                      </td>
                      <td>
                        <EditableCell value={r.start_date} type="date" nullable={false}
                          onCommit={v => patch({ start_date: v })}
                          formatter={v => v ? formatDate(v) : NULL_DATE} />
                      </td>
                      <td>
                        <EditableCell value={r.end_date} type="date"
                          onCommit={v => patch({ end_date: v })}
                          formatter={v => v ? formatDate(v) : NULL_DATE} />
                      </td>
                      <td className="hr-page__num">
                        <EditableCell value={r.monthly_salary} type="number" align="right"
                          onCommit={v => patch({ monthly_salary: v ?? 0 })} />
                      </td>
                      <td className="hr-page__num">
                        <EditableCell value={String(r.nb_of_salary)} type="select" options={NB_SAL_OPTIONS}
                          nullable={false} align="right"
                          onCommit={v => patch({ nb_of_salary: Number(v) })}
                          formatter={v => String(v)} />
                      </td>
                      <td className="hr-page__num">
                        <EditableCell value={r.activity_rate} type="number" step={0.01} align="right"
                          formatter={v => v != null ? `${formatNumber(Number(v) * 100, 0)}%` : '—'}
                          parser={s => Number(s) / 100}
                          onCommit={v => patch({ activity_rate: v })} />
                      </td>
                      <td className="hr-page__num">
                        <EditableCell value={r.vacation_days} type="number" step={1} min={0} max={50}
                          nullable={false} align="right"
                          onCommit={v => patch({ vacation_days: Number(v) })}
                          formatter={v => v != null ? String(v) : '—'} />
                      </td>
                      <td className="hr-page__num">{formatNumber(r.effective_yearly, 0)}</td>
                      <td className="hr-page__num">{formatNumber(r.yearly_100, 0)}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="hr-page__total-row">
                  <td className="hr-page__check-col"></td>
                  <td className="hr-page__col-sticky hr-page__col-sticky--actions"></td>
                  <td className="hr-page__col-sticky hr-page__col-sticky--person"><strong>{p('Total')}</strong></td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td className="hr-page__num"><strong>{formatNumber(totals.monthly_salary, 0)}</strong></td>
                  <td></td>
                  <td className="hr-page__num"><strong>{totals.activity_rate.toFixed(1)}</strong></td>
                  <td></td>
                  <td className="hr-page__num"><strong>{formatNumber(totals.effective_yearly, 0)}</strong></td>
                  <td className="hr-page__num"><strong>{formatNumber(totals.yearly_100, 0)}</strong></td>
                </tr>
              </tfoot>
            </table>
          </div>
          </CardShell>
        </>
      )}

      {error && (
        <div className="hr-modal__error" style={{ position: 'fixed', bottom: 20, right: 20, zIndex: 1000 }}>
          {error} <button onClick={() => setError(null)} style={{ marginLeft: 8 }}>×</button>
        </div>
      )}

      {bulkOpen && (
        <BulkUpdateContractsModal
          count={selected.size}
          onCancel={() => setBulkOpen(false)}
          onApply={async ({ field, value }) => {
            const updates = [...selected].map(id =>
              updateHrContract(tenantId, id, { [field]: value })
            );
            const results = await Promise.all(updates);
            const firstErr = results.find(r => r.error);
            if (firstErr) { setError(firstErr.error); return false; }
            setBulkOpen(false);
            clearSelection();
            refresh();
            return true;
          }}
        />
      )}

      {editingContract && (
        <ContractEditModal
          mode={editingContract === 'new' ? 'create' : 'edit'}
          defaultVacationDays={defaultVacationDays}
          people={people}
          initial={editingContract === 'new' ? null : editingContract}
          movements={movements}
          contracts={contracts}
          onGoToPlanning={setPage ? () => setPage('hr-planning') : undefined}
          onCancel={() => setEditingContract(null)}
          onSubmit={async (payload, closing = []) => {
            // Clôture du (des) contrat(s) précédent(s) demandée par la modale :
            // on l'applique AVANT la création, pour ne jamais laisser deux
            // contrats ouverts en base même si la création échoue ensuite.
            for (const c of closing) {
              const rc = await updateHrContract(tenantId, c.id, { end_date: c.end_date });
              if (rc.error) { setError(rc.error); return false; }
            }
            const r = editingContract === 'new'
              ? await createHrContract(tenantId, payload)
              : await updateHrContract(tenantId, editingContract.id, payload);
            if (r.error) { setError(r.error); return false; }
            setEditingContract(null);
            refresh();
            return true;
          }}
          onDelete={editingContract === 'new' ? undefined : async () => {
            const person = personIndex[editingContract.person_id];
            if (!window.confirm(p('Supprimer ce contrat de {{name}} ?', {
              name: `${person?.first_name || ''} ${person?.last_name || ''}`.trim(),
            }))) return false;
            const ok = await deleteHrContract(tenantId, editingContract.id);
            if (ok.error) { setError(ok.error); return false; }
            setEditingContract(null);
            refresh();
            return true;
          }}
          onDuplicate={editingContract === 'new' ? undefined : async () => {
            const r = editingContract;
            // Duplicate — same data, new open contract starting after the
            // original's end (or today if still open).
            const today = new Date().toISOString().slice(0, 10);
            let newStart = today;
            if (r.end_date) {
              const d = new Date(r.end_date);
              d.setDate(d.getDate() + 1);
              newStart = d.toISOString().slice(0, 10);
            }
            const ok = await createHrContract(tenantId, {
              person_id: r.person_id,
              start_date: newStart,
              end_date: null,
              title: r.title,
              department: r.department,
              monthly_salary: r.monthly_salary,
              nb_of_salary: r.nb_of_salary,
              activity_rate: r.activity_rate,
              vacation_days: r.vacation_days,
              cs_empr_pct: r.cs_empr_pct,
              cs_empl_pct: r.cs_empl_pct,
            });
            if (ok.error) { setError(ok.error); return false; }
            setEditingContract(null);
            refresh();
            return true;
          }}
        />
      )}
    </main>
  );
}



/* ── Modal Mass update : applique une même valeur sur un champ pour tous
   les contrats sélectionnés. ────────────────────────────────────────────── */
function BulkUpdateContractsModal({ count, onCancel, onApply }) {
  const { t } = useTranslation();
  const p = useP();
  const [fieldKey, setFieldKey] = useState(BULK_FIELDS[0].key);
  const [value, setValue] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);

  const field = BULK_FIELDS.find(f => f.key === fieldKey) || BULK_FIELDS[0];

  function changeField(k) {
    setFieldKey(k);
    setValue('');
    setErr(null);
  }

  // Coerce the raw input to the payload value expected by the API.
  function coerce() {
    const empty = value === '' || value == null;
    if (empty) {
      if (!field.nullable) return { error: 'Valeur requise pour ce champ.' };
      return { value: null };
    }
    switch (field.kind) {
      case 'text':
        return { value: String(value) };
      case 'date':
        return { value };  // 'YYYY-MM-DD'
      case 'number':
        return { value };  // backend accepts Decimal-as-string
      case 'decimal':      // activity_rate
        return { value };
      case 'int':
        return { value: Number(value) };
      case 'nb_sal':
        return { value: Number(value) };
      default:
        return { value };
    }
  }

  function renderInput() {
    if (field.kind === 'nb_sal') {
      return (
        <select className="hr-modal__input" value={value} onChange={e => setValue(e.target.value)}>
          <option value="">— choisir —</option>
          <option value="12">12</option>
          <option value="13">13</option>
          <option value="14">14</option>
        </select>
      );
    }
    if (field.kind === 'date') {
      return (
        <input className="hr-modal__input" type="date"
          value={value} onChange={e => setValue(e.target.value)} />
      );
    }
    if (field.kind === 'decimal') {
      return (
        <input className="hr-modal__input" type="number" step="0.01" min="0.01" max="1.5"
          value={value} onChange={e => setValue(e.target.value)} placeholder="ex: 1.0" />
      );
    }
    if (field.kind === 'int') {
      return (
        <input className="hr-modal__input" type="number" step="1" min="0"
          value={value} onChange={e => setValue(e.target.value)} />
      );
    }
    if (field.kind === 'number') {
      return (
        <input className="hr-modal__input" type="number" step="1"
          value={value} onChange={e => setValue(e.target.value)} />
      );
    }
    // text
    return (
      <input className="hr-modal__input" type="text"
        value={value} onChange={e => setValue(e.target.value)} />
    );
  }

  async function apply() {
    if (submitting) return;
    setErr(null);
    const c = coerce();
    if (c.error) { setErr(c.error); return; }
    setSubmitting(true);
    const ok = await onApply({ field: field.key, value: c.value });
    if (!ok) setSubmitting(false);
  }

  return (
    <div className="hr-modal__overlay">
      <div className="hr-modal" onClick={e => e.stopPropagation()}>
        <div className="hr-modal__header">
          <span className="hr-modal__title">
            Édition groupée — {count} {count > 1 ? p('contrats') : p('contrat')}
          </span>
          <button className="hr-modal__close" onClick={onCancel}>✕</button>
        </div>
        <div className="hr-modal__body">
          <div className="hr-modal__field">
            <label className="hr-modal__label">{p('Champ')}</label>
            <select className="hr-modal__input" value={fieldKey}
              onChange={e => changeField(e.target.value)}>
              {BULK_FIELDS.map(f => (
                <option key={f.key} value={f.key}>{f.label}</option>
              ))}
            </select>
          </div>
          <div className="hr-modal__field">
            <label className="hr-modal__label">
              Nouvelle valeur
              {field.nullable && <span className="hr-modal__hint"> {p('(vide = effacer)')}</span>}
            </label>
            {renderInput()}
          </div>
          {err && <div className="hr-modal__error">{err}</div>}
        </div>
        <div className="hr-modal__footer">
          <button className="hr-modal__btn" onClick={onCancel} disabled={submitting}>{t('common:actions.cancel')}</button>
          <button className="hr-modal__btn hr-modal__btn--primary"
            onClick={apply} disabled={submitting}>
            {submitting ? p('Application…') : p('Appliquer à {{count}}', { count })}
          </button>
        </div>
      </div>
    </div>
  );
}
